import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  useCreateSeedSender,
  useGetActiveMailboxes,
  useWarmupAccounts,
  useWarmupEnabled,
} from "@/hooks/use-mailbox";
import { useServices } from "@/services/hooks/useServices";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, Download, Plus, X } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { WarmupTab } from "../mailboxes/warmup-account-tab";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Check, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/_private/seed-list/$seed-list")({
  component: SeedList,
});

interface EmailInput {
  id: number;
  email: string;
  isValid?: boolean;
}

const RESTRICTED_DOMAINS = [
  // Gmail
  "gmail.com",
  // Outlook
  "outlook.com",
  "hotmail.com",
  "live.com",
  // Yahoo
  "yahoo.com",
  "ymail.com",
  "rocketmail.com",
  // ProtonMail
  "proton.me",
  "protonmail.com",
  // Zoho
  "zoho.com",
  // GMX
  "gmx.com",
  "gmx.us",
  "gmx.net",
  // AOL
  "aol.com",
  // Mail.com
  "mail.com",
  "email.com",
  "usa.com",
  // Tutanota
  "tutanota.com",
  "tutanota.de",
  "tutamail.com",
  "tuta.io",
  "keemail.me",
  // Yandex
  "yandex.com",
  "yandex.ru",
  "yandex.ua",
  "yandex.kz",
  "yandex.by",
];

function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;

  const domain = email.split("@")[1].toLowerCase();
  return !RESTRICTED_DOMAINS.includes(domain);
}

function SeedList() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [emailInputs, setEmailInputs] = useState<EmailInput[]>([
    { id: 1, email: "" },
  ]);
  const { data: warmupAccounts, isLoading } = useWarmupAccounts();
  const { data: activeMailboxes } = useGetActiveMailboxes();
  const { mailboxService } = useServices();
  const { mutate: createSeedSender } = useCreateSeedSender();
  const { data: warmupEnabled } = useWarmupEnabled();
  
  // New state for the updated UI
  
  const seedOptions = [500, 1000, 1500, 2000, 2500];
  
  // Calculate max allowed values based on account limits
  const maxAllowedSenders:number = warmupEnabled ? (warmupEnabled || 5) : 5;
  const maxAllowedSeeds:number = activeMailboxes ? (activeMailboxes || 500) : 500;
  const [seedCount, setSeedCount] = useState<number>(maxAllowedSeeds);
  const [senderCount, setSenderCount] = useState<number>(maxAllowedSenders);
  
  // Ensure selected values don't exceed limits
  useEffect(() => {
    if (senderCount > maxAllowedSenders) {
      setSenderCount(maxAllowedSenders);
    }
    
    // Find the highest allowed seed option that doesn't exceed maxAllowedSeeds
    const highestAllowedSeedOption = seedOptions
      .filter(option => option <= maxAllowedSeeds)
      .pop() || seedOptions[0];
      
    if (seedCount > highestAllowedSeedOption) {
      setSeedCount(highestAllowedSeedOption);
    }
  }, [warmupEnabled, maxAllowedSenders, maxAllowedSeeds]);

  const removeEmail = (id: number) => {
    setEmailInputs(emailInputs.filter((input) => input.id !== id));
  };

  const MAX_SENDERS = 50;

  const handleAddSenders = () => {
    // Filter out empty email inputs
    const nonEmptyEmails = emailInputs.filter(
      (input) => input.email.trim() !== ""
    );

    // Check total senders limit
    const currentSendersCount = warmupAccounts?.data?.length || 0;
    const newSendersCount = nonEmptyEmails.length;
    const totalSenders = currentSendersCount + newSendersCount;

    if (totalSenders > MAX_SENDERS) {
      toast.error(
        `You can only add up to ${MAX_SENDERS} senders. Please contact support to increase this limit.`
      );
      return;
    }

    const invalidEmails = nonEmptyEmails.filter(
      (input) => !isValidEmail(input.email)
    );
    if (invalidEmails.length > 0) {
      toast.error(
        "Please use business email addresses. Free email providers are not allowed."
      );
      return;
    }

    // Check for duplicate emails
    const emails = nonEmptyEmails.map((input) => input.email.toLowerCase());
    const hasDuplicates = new Set(emails).size !== emails.length;
    if (hasDuplicates) {
      toast.error("Duplicate email addresses are not allowed");
      return;
    }

    try {
      const allemail = nonEmptyEmails.map((input) => input.email);
      createSeedSender(allemail);
      toast.success(
        "Senders added successfully you can view them in the mailbox tab"
      );
    } catch (error) {
      toast.error("Failed to add senders");
    } finally {
      setIsDialogOpen(false);
    }
  };

  const handleDownloadCsv = async (provider: string = 'Google') => {
    const response = await mailboxService.getActiveMailboxesAsCsv();
    const blob = new Blob([response as unknown as BlobPart], {
      type: "text/csv",
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${provider.toLowerCase()}-seed-list.csv`;

    // Trigger download
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const addMoreEmail = () => {
    const newId = emailInputs.length + 1;
    setEmailInputs([...emailInputs, { id: newId, email: "" }]);
  };

  const updateEmail = (id: number, value: string) => {
    setEmailInputs(
      emailInputs.map((input) => {
        if (input.id === id) {
          const isValid = isValidEmail(value);
          return {
            ...input,
            email: value,
            isValid: value === "" ? undefined : isValid, // undefined for empty input
          };
        }
        return input;
      })
    );
  };

  return (
    <div className="w-full flex flex-col space-y-6">
      <h1 className="page-heading">Available Seeds</h1>

      {!warmupEnabled && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <AlertDescription className="text-red-800 h-full w-full flex items-center">
            Warmup is not enabled for your account. Please contact support to enable warmup features.
          </AlertDescription>
        </Alert>
      )}

      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-5 w-5 text-amber-600" />
        <AlertDescription className="text-amber-800 h-full w-full flex items-center">
          Please note that we can only track records for sender profiles that
          are registered in our system. If a sender profile that hasn't been
          added to the sender profiles section, we cannot perform warmup
          engagements. This can harm your domain and IP reputation. To avoid
          this, please ensure that you add your sender profile before emailing
          the seed list.
        </AlertDescription>
      </Alert>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-primary/80 flex items-center">
              <span className="bg-blue-100 text-blue-600 rounded-full w-7 h-7 inline-flex items-center justify-center mr-2">
                1
              </span>
              Select the number of seeds
              {maxAllowedSeeds < seedOptions[seedOptions.length - 1] && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Max: {maxAllowedSeeds}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {seedOptions.map((option) => (
                <Button
                  key={option}
                  variant={seedCount === option ? "default" : "outline"}
                  className={`min-w-[100px] ${seedCount === option ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-background hover:bg-blue-50"}`}
                  onClick={() => setSeedCount(option)}
                  disabled={option > maxAllowedSeeds || !warmupEnabled}
                >
                  {option}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-primary/80 flex items-center">
              <span className="bg-blue-100 text-blue-600 rounded-full w-7 h-7 inline-flex items-center justify-center mr-2">
                2
              </span>
              Select the number of senders
              <Badge variant="outline" className="ml-2 text-xs">
                Max: {maxAllowedSenders}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="px-4 py-6">
              <Slider
                defaultValue={[5]}
                max={maxAllowedSenders}
                min={1}
                step={1}
                value={[senderCount]}
                onValueChange={(value) => setSenderCount(value[0])}
                className="mb-6"
                disabled={!warmupEnabled}
              />
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">1</span>
                <span className="text-lg font-medium">{senderCount}</span>
                <span className="text-sm text-muted-foreground">{maxAllowedSenders}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          <SeedProviderCard
            provider="Google"
            icon="/googleIcon.png"
            seedCount={seedCount}
            senderCount={senderCount}
            availableSeeds={maxAllowedSeeds}
            onDownload={() => handleDownloadCsv('Google')}
            disabled={!warmupEnabled || activeMailboxes === 0}
          />
          <SeedProviderCard
            provider="Outlook"
            icon="/microsoftIcon.png"
            seedCount={0}
            senderCount={senderCount}
            availableSeeds={maxAllowedSeeds}
            disabled={true}
            onDownload={() => handleDownloadCsv('Outlook')}
          />
          <SeedProviderCard
            provider="Yahoo"
            icon="/images.jpg"
            seedCount={0}
            senderCount={senderCount}
            availableSeeds={maxAllowedSeeds}
            disabled={true}
            onDownload={() => handleDownloadCsv('Yahoo')}
          />
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Senders Profile</DialogTitle>
            <DialogDescription>
              Add email addresses to track their records
              <p className="mt-2 text-sm text-muted-foreground">
                {`You can add up to ${MAX_SENDERS} senders (${(warmupAccounts?.data?.length || 0)} used)`}
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4 max-h-[40vh] overflow-y-auto scrollbar-hide">
            {emailInputs.map((input) => (
              <div
                key={input.id}
                className="flex flex-col w-full items-center gap-2"
              >
                <div className="w-full flex">
                  <Input
                    type="email"
                    placeholder="user@domain.com"
                    value={input.email}
                    onChange={(e) => updateEmail(input.id, e.target.value)}
                    className={cn(
                      "border-input/20 focus-visible:ring-primary",
                      input.isValid === false &&
                        "border-red-500 focus-visible:ring-red-500"
                    )}
                  />
                  {emailInputs.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEmail(input.id)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {input.isValid === false && (
                  <p className="text-xs text-red-500 mt-1">
                    Please enter a valid business email address
                  </p>
                )}
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="w-full border-2 border-dashed border-muted-foreground/20 hover:border-primary/30 hover:bg-primary/5"
            onClick={addMoreEmail}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add more
          </Button>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setEmailInputs([{ id: 1, email: "" }]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSenders}
              className="bg-primary hover:bg-primary/90"
            >
              Add Senders
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface SeedProviderCardProps {
  provider: string;
  icon: string;
  seedCount: number;
  senderCount: number;
  disabled?: boolean;
  onDownload: () => void;
  availableSeeds: number;
}

function SeedProviderCard({ 
  provider, 
  icon, 
  seedCount, 
  senderCount, 
  disabled = false,
  availableSeeds,
  onDownload 
}: SeedProviderCardProps) {
  const actions = [
    "Scroll through the message",
    "Click the link",
    "Move out of Spam",
    "Mark as Starred",
    "Reply to the email",
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-blue-50 pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <img src={icon || "/placeholder.svg"} alt={`${provider} icon`} className="w-6 h-6" />
          {provider} Seeds
          <Badge variant="outline" className="ml-auto">
            {seedCount * senderCount > availableSeeds ? availableSeeds : seedCount * senderCount} accounts
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <Button 
          className="w-full mb-6 bg-blue-600 hover:bg-blue-700" 
          size="lg"
          disabled={disabled}
          onClick={onDownload}
        >
          Download now <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <div className="space-y-3 text-sm">
          {actions.map((action, index) => (
            <div key={index} className="flex items-center gap-2 text-muted-foreground">
              <Check className="h-4 w-4 text-green-500" />
              <span>{action}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
